// @ts-nocheck
/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { X, Printer, Bot, Target, ShieldCheck, Activity, Users, BrainCircuit, AlertTriangle, TrendingUp, TrendingDown, Calendar, Stethoscope, Zap, User, Ban, Gauge, ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';

// 💉 CHIRURGIE: Importe für Doppel-Schloss Store und Logic
import useAppStore, { useCalculationsSelector } from '../store/appStore';
import { evaluateRentabilitaet } from '../store/utils';

// ============================================================================
// HILFS-FUNKTIONEN (Self-Contained für das Modal)
// ============================================================================
const formatCurrency = (value: number, currency = 'EUR', decimals = 0): string => {
    if (isNaN(Number(value))) return `0 ${currency}`;
    return new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value || 0) + ` ${currency}`;
};

const formatPercent = (value: number, decimals = 1): string => {
    if (isNaN(Number(value))) return '0%';
    return (Number(value) * 100).toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }) + '%';
};

const formatNumber = (value: number, decimals = 1): string => {
    if (isNaN(Number(value))) return '0';
    return Number(value).toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

const formatDynamicPercent = (val: number) => {
    const formatted = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Math.abs(val)) + '%';
    return val > 0 ? '+' + formatted : (val < 0 ? '-' + formatted : '0,0%');
};

const getHealthStatus = (value: number, type: 'effizienz' | 'auslastung' | 'vk' | 'rentabilitaet', gehalt = 0, ueberschussProzent = 0) => {
    let level = 6;
    if (type === 'auslastung') {
        if (value >= 0.85) level = 1; else if (value >= 0.75) level = 2; else if (value >= 0.70) level = 3; else if (value >= 0.60) level = 4; else if (value >= 0.50) level = 5;
    } else if (type === 'effizienz') {
        if (value >= 1.00) level = 1; else if (value >= 0.95) level = 2; else if (value >= 0.89) level = 3; else if (value >= 0.72) level = 4; else if (value >= 0.63) level = 5;
    } else if (type === 'vk') {
        if (value >= 0.15) level = 1; else if (value >= 0.10) level = 2; else if (value >= 0.07) level = 3; else if (value >= 0.05) level = 4; else if (value >= 0.03) level = 5;
    } else if (type === 'rentabilitaet') {
        // 💉 CHIRURGIE: Auswertung über das Doppel-Schloss-Regelwerk
        const evalResult = evaluateRentabilitaet(gehalt, ueberschussProzent);
        switch (evalResult.status) {
            case 'Exzellent': level = 1; break;
            case 'Gut': level = 2; break;
            case 'Solide': level = 3; break;
            case 'Grenzwertig': level = 4; break;
            case 'Kritisch': 
            default: level = 6; break;
        }
    }
    
    switch (level) {
        case 1: return { colorClass: 'text-purple-400 led-purple', badgeClass: 'text-purple-400 border-purple-500/50 bg-purple-950/20' };
        case 2: return { colorClass: 'text-emerald-400 led-emerald', badgeClass: 'text-emerald-400 border-emerald-500/50 bg-emerald-950/20' };
        case 3: return { colorClass: 'text-lime-400 led-lime', badgeClass: 'text-lime-400 border-lime-500/50 bg-lime-950/20' };
        case 4: return { colorClass: 'text-yellow-400 led-amber', badgeClass: 'text-yellow-400 border-yellow-500/50 bg-yellow-950/20' };
        case 5: return { colorClass: 'text-orange-400 led-orange', badgeClass: 'text-orange-400 border-orange-500/50 bg-orange-950/20' };
        case 6: default: return { colorClass: 'text-red-400 led-red', badgeClass: 'text-red-400 border-red-500/50 bg-red-950/20' };
    }
};

const HjIcon = ({ status }: { status: string }) => {
    switch (status) {
        case 'ok': return <ThumbsUp className="w-4 h-4 text-emerald-500 drop-shadow-[0_0_5px_rgba(16,185,129,0.5)]" />;
        case 'warnung': return <AlertTriangle className="w-4 h-4 text-amber-500 drop-shadow-[0_0_5px_rgba(245,158,11,0.5)]" />;
        case 'schlecht': return <ThumbsDown className="w-4 h-4 text-red-500 drop-shadow-[0_0_5px_rgba(239,68,68,0.5)]" />;
        default: return <Ban className="w-4 h-4 text-slate-600" />;
    }
};

// ============================================================================
// KOMPONENTEN
// ============================================================================
const KICard = ({ type, title, children, icon }: { type: string, title: string, children: React.ReactNode, icon: string }) => {
    let bgClass = 'bg-[#111] border-[#333] text-slate-100'; 
    let titleClass = 'text-slate-400';
    
    if (type === 'excellent') {
        bgClass = 'bg-purple-950/20 border-purple-500/40 text-slate-100'; 
        titleClass = 'text-purple-400 led-purple';
    } else if (type === 'success') {
        bgClass = 'bg-emerald-950/20 border-emerald-500/40 text-slate-100'; 
        titleClass = 'text-emerald-400 led-emerald';
    } else if (type === 'warning') {
        bgClass = 'bg-amber-950/20 border-amber-500/40 text-slate-100'; 
        titleClass = 'text-amber-400 led-amber';
    } else if (type === 'critical' || type === 'danger') {
        bgClass = 'bg-red-950/20 border-red-500/40 text-slate-100'; 
        titleClass = 'text-red-400 led-red';
    }

    return (
        <div className={`p-6 rounded-xl border ${bgClass} shadow-md print:bg-white print:border-slate-300 print:text-black h-full`}>
            <div className="flex items-start gap-5 h-full">
                <span className="text-3xl mt-0.5 drop-shadow-md">{icon}</span>
                <div className="flex flex-col h-full w-full">
                    <h4 className={`font-black uppercase tracking-widest text-sm mb-3 print:text-black ${titleClass}`}>{title}</h4>
                    <div className="text-base leading-relaxed opacity-100 text-slate-200 font-sans print:text-black">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

export interface CockpitAiModalProps {
    isOpen: boolean;
    onClose: () => void;
    currencyCode: string;
    auslastungsVorgabe: number;
    ruecklagenZielFaktor: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pricingData: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ceoData: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    vitalsData: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    teamData: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hintConfig?: any;
    // 💉 CHIRURGIE: Doppel-Schloss Props
    gehalt?: number;
    ueberschussProzent?: number;
}

export default function CockpitAiModal({
    isOpen,
    onClose,
    currencyCode,
    auslastungsVorgabe,
    ruecklagenZielFaktor,
    pricingData,
    ceoData,
    vitalsData,
    teamData,
    hintConfig,
    gehalt = 0,
    ueberschussProzent = 0
}: CockpitAiModalProps) {
    const [mounted, setMounted] = useState(false);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [showAiAnalysis, setShowAiAnalysis] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [aiDiagnostics, setAiDiagnostics] = useState<any[]>([]);

    // 💉 CHIRURGIE: Sichere Store-Abfrage, falls Props fehlen sollten
    const calculationsStore = useCalculationsSelector();
    const storeUeberschussProzent = calculationsStore?.ueberschussProzent || 0;
    const unternehmerData = useAppStore(state => state.clientData?.unternehmerData);
    const storeGehalt = (unternehmerData?.entnahme || 0) + (unternehmerData?.sozialkosten || 0);

    const finalGehalt = gehalt > 0 ? gehalt : storeGehalt;
    const finalUeberschuss = ueberschussProzent !== 0 ? ueberschussProzent : storeUeberschussProzent;

    const fmt = (val: number) => new Intl.NumberFormat('de-DE').format(Math.round(val));

    useEffect(() => { setMounted(true); }, []);

    useEffect(() => {
        if (isOpen) {
            setShowAiAnalysis(false);
            setAiError(null);
            setAiDiagnostics([]);
            handleStartKiPruefung();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleStartKiPruefung = async () => {
        setIsAiLoading(true);
        setAiError(null);
        setShowAiAnalysis(false); 
        setAiDiagnostics([]);

        try {
            const functions = getFunctions(getApp(), 'europe-west1');
            const generateAi = httpsCallable(functions, 'generateCockpitDiagnosticAi');
            
            const pricingDelta = pricingData?.stundenfaktor > 0
                ? ((pricingData?.echterStundensatz - pricingData?.stundenfaktor) / pricingData?.stundenfaktor) * 100
                : 0;

            const pricingDeltaMat = pricingData?.targetMatPreis > 0
                ? ((pricingData?.echterStundensatzMat - pricingData?.targetMatPreis) / pricingData?.targetMatPreis) * 100
                : 0;

            // 💉 CHIRURGIE: Payload an das Backend inkl. UI-Status aus dem Doppel-Schloss
            const payloadMetrics = {
                pricingData: {
                    stundenfaktor: pricingData?.stundenfaktor || 0,
                    echterStundensatz: pricingData?.echterStundensatz || 0,
                    pricingDeltaProzent: pricingDelta,
                    stundenfaktorInklMat: pricingData?.targetMatPreis || 0,
                    echterStundensatzInklMat: pricingData?.echterStundensatzMat || 0,
                    pricingDeltaProzentMat: pricingDeltaMat
                },
                ceoData: {
                    nettoKosten: ceoData?.nettoKosten || 0,
                    nettoZiel: ceoData?.nettoZiel || 0,
                    nettoIst: ceoData?.nettoIst || 0,
                    gehalt: finalGehalt, 
                    ueberschussProzent: finalUeberschuss
                },
                uiStatus: {
                    statusTitel: hintConfig?.title || 'Unbekannt',
                    statusText: hintConfig?.text || 'Kein Status',
                    rentabilitaetStatus: evaluateRentabilitaet(finalGehalt, finalUeberschuss).status
                },
                vitalsData: {
                    globalEffizienz: vitalsData?.globalEffizienz || 0,
                    globalAuslastungDecimal: vitalsData?.globalAuslastungDecimal || 0,
                    globalVkFaktor: vitalsData?.globalVkFaktor || 0,
                    realeRentabilitaet: vitalsData?.realeRentabilitaet || 0
                },
                teamData: {
                    totalKrank: teamData?.totalKrank || 0,
                    ausfallKostenVerlust: teamData?.ausfallKostenVerlust || 0,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    employees: (teamData?.realEmployees || []).map((e: any) => ({
                        name: e?.name || 'Unbekannt',
                        effizienz: e?.effizienz || 0,
                        deltaNetto: e?.deltaNetto || 0,
                        krank: e?.krank || 0
                    }))
                },
                auslastungsVorgabe: auslastungsVorgabe || 0,
                ruecklagenZielFaktor: ruecklagenZielFaktor || 0
            };

            const res = await generateAi({ 
                metrics: payloadMetrics
            });
            
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const payload = (res.data as any)?.diagnostics;
            if (payload && Array.isArray(payload)) {
                setAiDiagnostics(payload);
                setShowAiAnalysis(true);
            } else {
                throw new Error("Das AI-Modell hat keine gültigen Diagnose-Daten zurückgeliefert.");
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            console.error("KI-Prüfung fehlgeschlagen:", error);
            setAiError(error?.message || "Fehler bei der Server-Kommunikation. Bitte prüfe deine Internetverbindung.");
        } finally {
            setIsAiLoading(false);
        }
    };

    const renderAiCard = (category: string) => {
        if (!showAiAnalysis) return null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const diag = aiDiagnostics.find((d: any) => d.category === category);
        if (!diag) return <div className="hidden xl:block"></div>; 
        return (
            <div className="h-full">
                <KICard type={diag.type} title={diag.title} icon={diag.icon}>
                    {diag.text}
                </KICard>
            </div>
        );
    };

    if (!isOpen || !mounted) return null;

    const deficitNetto = ceoData.nettoKosten - ceoData.nettoIst;
    const isDeficit = deficitNetto > 0;

    // CEO Matrix Berechnungen
    const zielRuecklagenEur = ceoData.nettoZiel - ceoData.nettoKosten;
    const istRuecklagenEur = ceoData.nettoIst - ceoData.nettoKosten;
    const istRuecklagenPct = ceoData.nettoKosten > 0 ? (istRuecklagenEur / ceoData.nettoKosten) * 100 : 0;
    
    const deltaZielEur = ceoData.nettoIst - ceoData.nettoZiel;
    const deltaZielPct = ceoData.nettoZiel > 0 ? (deltaZielEur / ceoData.nettoZiel) * 100 : 0;

    // ========================================================================
    // KORRIGIERTE RENDITE-BERECHNUNG (EXAKT WIE IM HAUPT-COCKPIT TACHO)
    // ========================================================================
    const stdSoll = pricingData?.stundenfaktor || 0;
    const stdIst = pricingData?.echterStundensatz || 0;
    
    const basisRuecklage = pricingData?.basisRuecklage ?? (ruecklagenZielFaktor * 100); 
    const costPerHour1 = stdSoll * (1 - (basisRuecklage / 100));
    const realProfit1 = stdIst - costPerHour1;
    const istMargin1 = stdIst > 0 ? (realProfit1 / stdIst) * 100 : 0;

    const matSoll = pricingData?.targetMatPreis || 0;
    const matIst = pricingData?.echterStundensatzMat || 0;
    const targetMatRuecklage = pricingData?.targetMatRuecklage ?? (ruecklagenZielFaktor * 100);
    const costPerHour2 = matSoll * (1 - (targetMatRuecklage / 100));
    const realProfit2 = matIst - costPerHour2;
    const istMargin2 = matIst > 0 ? (realProfit2 / matIst) * 100 : 0;

    // Farb-Logik: Gebildete Rücklage
    let ruecklageBg = 'bg-red-950/20 border-red-500/30';
    let ruecklageColor = 'text-red-500';
    let ruecklageText = 'text-red-400 led-red';
    let ruecklageIcon = <TrendingDown className="w-3 h-3"/>;

    if (istRuecklagenEur >= zielRuecklagenEur && zielRuecklagenEur > 0) {
        ruecklageBg = 'bg-emerald-950/20 border-emerald-500/30';
        ruecklageColor = 'text-emerald-500';
        ruecklageText = 'text-emerald-400 led-emerald';
        ruecklageIcon = <ThumbsUp className="w-3 h-3"/>;
    } else if (istRuecklagenEur >= 0) {
        ruecklageBg = 'bg-amber-950/20 border-amber-500/30';
        ruecklageColor = 'text-amber-500';
        ruecklageText = 'text-amber-400 led-amber';
        ruecklageIcon = <ThumbsUp className="w-3 h-3"/>;
    }

    // Farb-Logik: Delta zum Ziel
    let deltaBg = 'bg-red-950/20 border-red-500/30';
    let deltaColor = 'text-red-500';
    let deltaText = 'text-red-400 led-red';

    if (deltaZielEur >= 0) {
        deltaBg = 'bg-emerald-950/20 border-emerald-500/30';
        deltaColor = 'text-emerald-500';
        deltaText = 'text-emerald-400 led-emerald';
    } else if (istRuecklagenEur >= 0) {
        deltaBg = 'bg-orange-950/20 border-orange-500/30';
        deltaColor = 'text-orange-500';
        deltaText = 'text-orange-400 led-orange';
    }

    return createPortal(
        <div className="print-modal-wrapper fixed inset-0 z-[2147483647] flex justify-center bg-black/90 backdrop-blur-sm pt-20 pb-8 px-4 overflow-y-auto font-sans selection:bg-phantom-gold selection:text-black">
            <div className="print-modal-inner relative carbon-panel carbon-weave-bg border border-[#333] rounded-2xl w-full max-w-[1300px] shadow-2xl flex flex-col h-auto min-h-min text-slate-200">
                
                {/* HEADER */}
                <div className="sticky top-0 z-50 p-5 bg-[#050505] border-b border-[#333] flex flex-wrap justify-between items-center gap-6 print-hide rounded-t-2xl shadow-md">
                    <div className="flex items-center gap-3">
                        <div className="metal-badge p-2 bg-black">
                            <Bot className="text-phantom-gold w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-black tracking-widest uppercase text-lg text-white leading-tight">KI Insight Dashboard</h3>
                            <span className="text-[10px] text-phantom-gold font-bold uppercase tracking-widest">Executive Business Analysis</span>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {!showAiAnalysis && (
                            <button 
                                onClick={handleStartKiPruefung}
                                disabled={isAiLoading}
                                className={`flex items-center gap-2 metal-badge bg-black hover:bg-[#111] border-[#333] text-phantom-gold font-bold uppercase tracking-widest text-[10px] px-5 py-3 rounded-xl transition-all shadow-md ${isAiLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <BrainCircuit className={`w-4 h-4 ${isAiLoading ? 'animate-pulse' : ''}`} /> 
                                {isAiLoading ? 'Sammle Telemetrie...' : 'KI-Analyse anfordern'}
                            </button>
                        )}
                        <button onClick={() => window.print()} className="flex items-center gap-2 metal-badge hover:bg-[#111] text-white font-bold text-[10px] px-5 py-3 rounded-xl transition-all shadow-md uppercase tracking-widest border border-[#333]">
                            <Printer className="w-4 h-4" /> Ausdrucken
                        </button>
                        <button onClick={onClose} className="p-2.5 bg-black hover:bg-[#111] border border-[#333] text-slate-400 hover:text-white rounded-full transition-colors shadow-inner">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* INHALT */}
                <div className="carbon-content p-6 md:p-10 space-y-10 relative min-h-[600px]">
                    
                    {/* VISUELLER LADE-BILDSCHIRM */}
                    {isAiLoading && (
                        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md rounded-b-2xl transition-all duration-300">
                            <div className="p-10 border border-phantom-gold/30 rounded-2xl bg-[#0a0a0a] shadow-[0_0_50px_rgba(230,194,153,0.15)] flex flex-col items-center justify-center animate-in zoom-in-95 duration-500">
                                <Loader2 className="w-16 h-16 text-phantom-gold mb-6 animate-spin" />
                                <h3 className="font-black tracking-widest uppercase text-xl text-phantom-gold mb-2">KI analysiert Daten...</h3>
                                <p className="text-sm text-slate-400 text-center max-w-xs leading-relaxed">Bitte warten. Das neuronale Netz wertet die Cockpit-Telemetrie aus.</p>
                            </div>
                        </div>
                    )}

                    {aiError && (
                        <div className="bg-red-950/40 border border-red-500/50 rounded-xl p-6 shadow-inner flex flex-col items-center justify-center text-center animate-in fade-in zoom-in-95 mt-10">
                            <AlertTriangle className="w-10 h-10 text-red-500 mb-4" />
                            <h4 className="font-black text-red-400 led-red text-lg uppercase tracking-widest mb-2">Server-Fehler: Diagnose blockiert</h4>
                            <p className="text-sm text-red-200/90 font-mono mb-6 max-w-lg">{aiError}</p>
                            <button 
                                onClick={handleStartKiPruefung}
                                className="flex items-center gap-2 metal-badge bg-black hover:bg-[#111] border-[#333] text-white font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-xl transition-all shadow-md"
                            >
                                <BrainCircuit className="w-4 h-4" /> Erneut versuchen
                            </button>
                        </div>
                    )}

                    {/* Nur rendern, wenn nicht am Laden und kein harter Fehler im Weg ist */}
                    {(!isAiLoading && !aiError) && (
                        <>
                            {/* ============================================================ */}
                            {/* BLOCK 1 & 2: PRICING & CEO MATRIX (HARMONISIERTES LAYOUT) */}
                            {/* ============================================================ */}
                            <div className="flex flex-col gap-6">
                                
                                {/* OBEN: DIE DATEN-BOXEN (Breakpoints auf xl gesetzt um quetschen zu verhindern) */}
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
                                    
                                    {/* Box 1: Pricing Visual */}
                                    <div className="bg-[#0a0a0a] border border-[#333] p-5 rounded-xl shadow-inner print:bg-white print:border-slate-300 flex flex-col justify-between h-full w-full overflow-hidden">
                                        <div>
                                            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                                <Gauge className="w-3 h-3 text-phantom-gold" /> Pricing-Basis
                                            </h4>
                                            <div className="flex flex-col sm:flex-row gap-4">
                                                
                                                {/* Ohne Material */}
                                                <div className="flex-1 bg-[#111] border border-[#222] rounded-lg p-4 shadow-sm flex flex-col justify-between">
                                                    <div>
                                                        <span className="block text-[10px] uppercase tracking-widest text-slate-400 mb-4">Ohne Material</span>
                                                        <div className="flex justify-between items-baseline mb-3">
                                                            <span className="text-[11px] text-slate-500 uppercase font-bold">Soll</span>
                                                            <span className="font-mono-numbers text-xl font-black text-phantom-gold led-gold">{formatNumber(stdSoll, 0)} <span className="text-[10px] text-phantom-gold/60">{currencyCode}</span></span>
                                                        </div>
                                                        <div className="flex justify-between items-baseline pt-3 border-t border-[#333]">
                                                            <span className="text-[11px] text-slate-500 uppercase font-bold">Ist</span>
                                                            <span className={`font-mono-numbers text-xl font-black ${stdIst < stdSoll ? 'text-red-400 led-red' : 'text-emerald-400 led-emerald'}`}>
                                                                {formatNumber(stdIst, 0)} <span className="text-[10px] opacity-70">{currencyCode}</span>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Rendite Container - Gestapelt um Overflows zu verhindern */}
                                                    <div className="mt-4 pt-4 border-t border-[#222] flex flex-col gap-2">
                                                        <div className="flex justify-between items-center bg-[#0a0a0a] border border-[#222] rounded-md px-3 py-2 shadow-inner">
                                                            <span className="text-[9px] text-slate-500 uppercase font-bold tracking-widest">Soll-Rendite</span>
                                                            <span className="text-[11px] font-mono-numbers font-bold text-phantom-gold/90">{basisRuecklage.toFixed(1)}%</span>
                                                        </div>
                                                        <div className={`flex justify-between items-center bg-[#0a0a0a] border rounded-md px-3 py-2 shadow-inner ${istMargin1 >= basisRuecklage ? 'border-emerald-500/20' : 'border-red-500/20'}`}>
                                                            <span className="text-[9px] text-slate-500 uppercase font-bold tracking-widest">Ist-Rendite</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-[11px] font-mono-numbers font-bold ${istMargin1 >= basisRuecklage ? 'text-emerald-400' : 'text-red-400'}`}>
                                                                    {istMargin1.toFixed(1)}%
                                                                </span>
                                                                <span className={`text-[9px] font-mono-numbers font-bold px-1.5 py-0.5 rounded ${istMargin1 >= basisRuecklage ? 'bg-emerald-950/40 text-emerald-500' : 'bg-red-950/40 text-red-500'}`}>
                                                                    {(istMargin1 - basisRuecklage) > 0 ? '+' : ''}{(istMargin1 - basisRuecklage).toFixed(1)}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Inkl Material */}
                                                <div className="flex-1 bg-[#111] border border-[#222] rounded-lg p-4 shadow-sm flex flex-col justify-between">
                                                    <div>
                                                        <span className="block text-[10px] uppercase tracking-widest text-slate-400 mb-4">Inkl. Material</span>
                                                        <div className="flex justify-between items-baseline mb-3">
                                                            <span className="text-[11px] text-slate-500 uppercase font-bold">Soll</span>
                                                            <span className="font-mono-numbers text-xl font-black text-phantom-gold led-gold">{formatNumber(matSoll, 0)} <span className="text-[10px] text-phantom-gold/60">{currencyCode}</span></span>
                                                        </div>
                                                        <div className="flex justify-between items-baseline pt-3 border-t border-[#333]">
                                                            <span className="text-[11px] text-slate-500 uppercase font-bold">Ist</span>
                                                            <span className={`font-mono-numbers text-xl font-black ${matIst < matSoll ? 'text-red-400 led-red' : 'text-emerald-400 led-emerald'}`}>
                                                                {formatNumber(matIst, 0)} <span className="text-[10px] opacity-70">{currencyCode}</span>
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Rendite Container - Gestapelt um Overflows zu verhindern */}
                                                    <div className="mt-4 pt-4 border-t border-[#222] flex flex-col gap-2">
                                                        <div className="flex justify-between items-center bg-[#0a0a0a] border border-[#222] rounded-md px-3 py-2 shadow-inner">
                                                            <span className="text-[9px] text-slate-500 uppercase font-bold tracking-widest">Soll-Rendite</span>
                                                            <span className="text-[11px] font-mono-numbers font-bold text-phantom-gold/90">{targetMatRuecklage.toFixed(1)}%</span>
                                                        </div>
                                                        <div className={`flex justify-between items-center bg-[#0a0a0a] border rounded-md px-3 py-2 shadow-inner ${istMargin2 >= targetMatRuecklage ? 'border-emerald-500/20' : 'border-red-500/20'}`}>
                                                            <span className="text-[9px] text-slate-500 uppercase font-bold tracking-widest">Ist-Rendite</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-[11px] font-mono-numbers font-bold ${istMargin2 >= targetMatRuecklage ? 'text-emerald-400' : 'text-red-400'}`}>
                                                                    {istMargin2.toFixed(1)}%
                                                                </span>
                                                                <span className={`text-[9px] font-mono-numbers font-bold px-1.5 py-0.5 rounded ${istMargin2 >= targetMatRuecklage ? 'bg-emerald-950/40 text-emerald-500' : 'bg-red-950/40 text-red-500'}`}>
                                                                    {(istMargin2 - targetMatRuecklage) > 0 ? '+' : ''}{(istMargin2 - targetMatRuecklage).toFixed(1)}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Box 2: CEO Matrix */}
                                    <div className="bg-[#0a0a0a] border border-[#333] p-5 rounded-xl shadow-inner print:bg-white print:border-slate-300 flex flex-col justify-between h-full w-full">
                                        <div>
                                            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                                <Target className="w-3 h-3 text-phantom-gold" /> Finanzielle Gesundheit (Mtl. Ø Netto)
                                            </h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center mb-4">
                                                <div className="bg-[#111] border border-[#222] rounded-lg p-3 shadow-sm">
                                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 mb-1">Break-Even</span>
                                                    <span className="font-mono-numbers text-lg font-black text-slate-300">{formatCurrency(ceoData.nettoKosten, currencyCode)}</span>
                                                </div>
                                                <div className="bg-[#111] border border-[#222] rounded-lg p-3 shadow-sm">
                                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 mb-1">Ist-Leistung</span>
                                                    <span className={`font-mono-numbers text-lg font-black ${!isDeficit ? 'text-emerald-400' : 'text-amber-400'}`}>{formatCurrency(ceoData.nettoIst, currencyCode)}</span>
                                                </div>
                                                <div className="bg-phantom-gold/10 border border-phantom-gold/30 rounded-lg p-3 shadow-sm flex flex-col justify-center">
                                                    <span className="block text-[8px] uppercase tracking-widest text-phantom-gold/70 mb-1 leading-tight">Ziel (+{formatPercent(ruecklagenZielFaktor, 0)})</span>
                                                    <span className="font-mono-numbers text-lg font-black text-phantom-gold led-gold leading-tight">{formatCurrency(ceoData.nettoZiel, currencyCode)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-3 mt-auto pt-2">
                                            <div className={`flex-1 p-4 rounded-xl border flex justify-between items-center shadow-inner ${ruecklageBg}`}>
                                                <div className="flex flex-col">
                                                    <span className={`text-[10px] uppercase tracking-widest font-black flex items-center gap-2 ${ruecklageColor}`}>
                                                        {ruecklageIcon}
                                                        {istRuecklagenEur >= 0 ? 'Gebildete Rücklage' : 'Liquiditäts-Lücke'}
                                                    </span>
                                                    <span className={`text-[10px] font-bold mt-1.5 opacity-80 ${ruecklageColor}`}>
                                                        {istRuecklagenPct > 0 ? '+' : ''}{Math.abs(istRuecklagenPct).toFixed(1)}% der Kosten
                                                    </span>
                                                </div>
                                                <span className={`font-mono-numbers font-black text-2xl whitespace-nowrap ${ruecklageText}`}>
                                                    {istRuecklagenEur >= 0 ? '+' : '-'}{formatCurrency(Math.abs(istRuecklagenEur), currencyCode)}
                                                </span>
                                            </div>
                                            <div className={`flex-1 p-4 rounded-xl border flex justify-between items-center shadow-inner ${deltaBg}`}>
                                                <div className="flex flex-col">
                                                    <span className={`text-[10px] uppercase tracking-widest font-black ${deltaColor}`}>
                                                        Delta zum Ziel
                                                    </span>
                                                    <span className={`text-[10px] font-bold mt-1.5 opacity-80 ${deltaColor}`}>
                                                        {deltaZielPct > 0 ? '+' : ''}{Math.abs(deltaZielPct).toFixed(1)}% vom Ziel
                                                    </span>
                                                </div>
                                                <span className={`font-mono-numbers font-black text-2xl whitespace-nowrap ${deltaText}`}>
                                                    {deltaZielEur > 0 ? '+' : '-'}{formatCurrency(Math.abs(deltaZielEur), currencyCode)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* UNTEN: DIE KI-KARTEN (Auf exakt derselben Höhe durch Grid Stretch) */}
                                {showAiAnalysis && (
                                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
                                        {renderAiCard('pricing')}
                                        {renderAiCard('ceo')}
                                    </div>
                                )}
                            </div>

                            {/* BLOCK 3: VITALS + AI (direkt drunter) */}
                            <div className="pt-4">
                                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4 border-b border-[#333] pb-2 flex items-center gap-2">
                                    <Activity className="w-3 h-3 text-phantom-gold" /> Performance Vitals
                                </h4>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                    {[
                                        { label: "Ø Team-Effizienz", val: vitalsData.globalEffizienz, fmt: (v:number) => (v*100).toFixed(1), type: 'effizienz', icon: Zap },
                                        { label: "Ø Umsatzauslastung", val: vitalsData.globalAuslastungDecimal, fmt: (v:number) => (v*100).toFixed(1), type: 'auslastung', icon: Activity },
                                        { label: "Verkaufsfaktor", val: vitalsData.globalVkFaktor, fmt: (v:number) => (v*100).toFixed(1), type: 'vk', icon: TrendingUp },
                                        { label: "Reale Rentabilität", val: vitalsData.realeRentabilitaet, fmt: (v:number) => (v*100).toFixed(1), type: 'rentabilitaet', icon: ShieldCheck }
                                    ].map((metric, i) => {
                                        // 💉 CHIRURGIE: gehalt und ueberschussProzent werden hier ans Frontend übergeben, um die Farben korrekt zu setzen
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        const status = getHealthStatus(metric.val, metric.type as any, finalGehalt, finalUeberschuss);
                                        return (
                                            <div key={i} className="bg-[#111] border border-[#222] p-4 flex flex-col items-center justify-center text-center rounded-xl shadow-inner">
                                                <metric.icon className="w-5 h-5 text-[#555] mb-2" />
                                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">{metric.label}</span>
                                                <span className={`text-2xl font-black font-mono-numbers mb-2 ${status.colorClass}`}>{metric.fmt(metric.val)}<span className="text-xs opacity-50">%</span></span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-4">
                                    {renderAiCard('vitals')}
                                </div>
                            </div>

                            {/* BLOCK 4: TEAM LISTE + AI (direkt drunter) */}
                            <div className="pt-4">
                                <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4 border-b border-[#333] pb-2 flex items-center gap-2">
                                    <Users className="w-3 h-3 text-phantom-gold" /> Mitarbeiter Performance & Ausfälle
                                </h4>
                                
                                {/* Ausfall Metriken */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                                    <div className="bg-[#111] border border-[#222] p-4 flex flex-col items-center justify-center text-center rounded-xl shadow-inner">
                                        <Calendar className="w-5 h-5 text-amber-500/60 mb-2" />
                                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Urlaubstage (Team)</span>
                                        <span className="text-2xl font-black font-mono-numbers text-amber-400 led-amber">{formatNumber(teamData.totalUrlaub || 0, 0)} <span className="text-xs font-sans text-amber-500/50">Tage</span></span>
                                    </div>
                                    <div className="bg-[#111] border border-[#222] p-4 flex flex-col items-center justify-center text-center rounded-xl shadow-inner">
                                        <Stethoscope className="w-5 h-5 text-red-500/60 mb-2" />
                                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Krankheitstage (Team)</span>
                                        <span className="text-2xl font-black font-mono-numbers text-red-400 led-red">{formatNumber(teamData.totalKrank || 0, 0)} <span className="text-xs font-sans text-red-500/50">Tage</span></span>
                                    </div>
                                    <div className="bg-[#111] border border-red-900/30 p-4 flex flex-col items-center justify-center text-center rounded-xl shadow-inner">
                                        <TrendingDown className="w-5 h-5 text-red-500/60 mb-2" />
                                        <span className="text-[9px] font-bold text-red-500 uppercase tracking-widest mb-1">Ø Verlust (Krankheit)</span>
                                        <span className="text-2xl font-black font-mono-numbers text-red-500 led-red">-{formatCurrency(teamData.ausfallKostenVerlust || 0, currencyCode, 0)}</span>
                                    </div>
                                </div>

                                {/* Mitarbeiter Liste */}
                                <div className="flex flex-col gap-2">
                                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                    {teamData.realEmployees.map((emp: any) => {
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        const effStatus = getHealthStatus(emp.effizienz, 'effizienz' as any, finalGehalt, finalUeberschuss);
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        const auslStatus = getHealthStatus(emp.auslastung, 'auslastung' as any, finalGehalt, finalUeberschuss);
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        const vkStatus = getHealthStatus(emp.vk, 'vk' as any, finalGehalt, finalUeberschuss);
                                        const isProfit = emp.deltaNetto >= 0;

                                        return (
                                            <div key={emp.id} className={`flex items-center gap-4 p-4 rounded-xl transition-colors border ${emp.isChef ? 'bg-[#E6C299]/5 border-phantom-gold/30' : 'bg-black/40 border-[#222] hover:bg-[#111]'}`}>
                                                
                                                {/* 1. Name & Status */}
                                                <div className="w-[18%] flex items-center gap-3 text-left">
                                                    <div className={`p-2 rounded-lg border shrink-0 ${emp.isChef ? 'bg-black border-phantom-gold text-phantom-gold shadow-[0_0_10px_rgba(230,194,153,0.3)]' : 'bg-[#1a1a1a] border-[#333] text-slate-400'}`}>
                                                        <User className="w-4 h-4" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className={`text-sm font-bold uppercase tracking-wider ${emp.isChef ? 'text-phantom-gold' : 'text-slate-200'}`}>
                                                            {emp.name}
                                                        </span>
                                                        {!emp.isChef && (
                                                            <span className={`text-[9px] font-bold uppercase tracking-widest mt-0.5 ${emp.personalStatusColor}`}>
                                                                {emp.personalStatusText}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* 2. Effizienz */}
                                                <div className={`w-[11%] text-center font-mono-numbers text-base font-black ${effStatus.colorClass}`}>
                                                    {(emp.effizienz * 100).toFixed(1)}%
                                                </div>

                                                {/* 3. VK-Faktor */}
                                                <div className={`w-[10%] text-center font-mono-numbers text-base font-black ${vkStatus.colorClass}`}>
                                                    {(emp.vk * 100).toFixed(1)}%
                                                </div>

                                                {/* 4. Auslastung */}
                                                <div className={`w-[11%] text-center font-mono-numbers text-base font-black ${auslStatus.colorClass}`}>
                                                    {(emp.auslastung * 100).toFixed(1)}%
                                                </div>

                                                {/* 5. Krank / Urlaub */}
                                                <div className="w-[12%] flex items-center justify-center gap-5 text-center">
                                                    <div className="flex flex-col items-center">
                                                        <Stethoscope className="w-4 h-4 text-red-500/60 mb-1" />
                                                        <span className="text-[15px] font-bold font-mono-numbers text-red-400">{emp.krank}</span>
                                                    </div>
                                                    <div className="w-px h-8 bg-slate-700/50"></div>
                                                    <div className="flex flex-col items-center">
                                                        <Calendar className="w-4 h-4 text-amber-500/60 mb-1" />
                                                        <span className="text-[15px] font-bold font-mono-numbers text-amber-400">{emp.urlaub}</span>
                                                    </div>
                                                </div>

                                                {/* 6. Hj1 & Hj2 Ampeln */}
                                                <div className="w-[8%] text-center flex items-center justify-center gap-3">
                                                    <HjIcon status={emp.hj1} />
                                                    <span className="text-[#333]">|</span>
                                                    <HjIcon status={emp.hj2} />
                                                </div>

                                                {/* 7. Bonus (Getrennt Hj1 / Hj2) */}
                                                <div className="w-[12%] text-center font-mono-numbers font-bold text-sm flex items-center justify-center gap-2">
                                                    {emp.isChef ? (
                                                        <span className="text-slate-600">—</span>
                                                    ) : (
                                                        <>
                                                            <span className={emp.bonusHj1 >= 0 ? "text-emerald-400" : "text-red-400"}>
                                                                {emp.bonusHj1 > 0 ? '+' : ''}{fmt(emp.bonusHj1)}
                                                            </span>
                                                            <span className="text-[#333]">|</span>
                                                            {emp.hasHj2 ? (
                                                                <span className={emp.bonusHj2 >= 0 ? "text-emerald-400" : "text-red-400"}>
                                                                    {emp.bonusHj2 > 0 ? '+' : ''}{fmt(emp.bonusHj2)}
                                                                </span>
                                                            ) : (
                                                                <Ban className="w-3 h-3 text-slate-700" />
                                                            )}
                                                        </>
                                                    )}
                                                </div>

                                                {/* 8. Delta Netto + Prozent */}
                                                <div className="w-[14%] text-right ml-auto flex justify-end">
                                                    <div className={`metal-badge px-3 py-1.5 flex flex-col items-center justify-center gap-1 ${isProfit ? 'bg-emerald-950/20 border-emerald-500/30' : 'bg-red-950/20 border-red-500/30'}`}>
                                                        <span className={`text-base font-black font-mono-numbers leading-none ${isProfit ? 'led-emerald' : 'led-red'}`}>
                                                            {isProfit ? '+' : ''}{fmt(emp.deltaNetto)}
                                                        </span>
                                                        <span className={`text-[10px] font-bold font-mono-numbers opacity-80 leading-none ${isProfit ? 'text-emerald-500' : 'text-red-500'}`}>
                                                            {formatDynamicPercent(emp.prozentDelta)}
                                                        </span>
                                                    </div>
                                                </div>

                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="mt-4">
                                {renderAiCard('team')}
                            </div>
                        </>
                    )}

                </div>
            </div>
        </div>,
        document.body
    );
}